import { Check, Maximize2, Trash2 } from 'lucide-react';
import { Card, Tooltip } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import WidgetLibraryMiniPreview from './WidgetLibraryMiniPreview';
import DashboardButton from './DashboardButton';

const WidgetLibraryGridCard = ({
  widget,
  isSelected,
  disabled = false,
  disabledTooltip,
  onCardToggle,
  onCardKeyDown,
  onActionButtonClick,
  onExpandPreviewClick,
  expandPreviewAriaLabel,
}) => {
  const { t } = useTranslation('dashboards');
  return (
    <Tooltip content={disabledTooltip} disabled={!disabled} size="compact" triggerClassName="flex h-full w-full">
    <Card
    role="button"
    tabIndex={disabled ? -1 : 0}
    aria-pressed={isSelected}
    aria-disabled={disabled || undefined}
    onClick={onCardToggle(widget)}
    onKeyDown={onCardKeyDown(widget)}
    className={`group/grid-lib h-full w-full rounded-xl border-[1.5px] shadow-none outline-none transition-colors focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-surface ${
      disabled
        ? 'cursor-not-allowed border-edge-strong opacity-50 dark:border-edge'
        : 'cursor-pointer'
    } ${
      !disabled && isSelected
        ? '!border-brand-500 bg-brand-50/20 dark:bg-brand-950/35'
        : ''
    } ${
      !disabled && !isSelected
        ? 'border-edge-strong hover:border-brand-300/60 dark:border-edge dark:hover:border-brand-600/40'
        : ''
    }`}
  >
    <div className="relative flex h-full min-h-0 flex-col p-3">
      <div className="relative mb-2 min-h-[96px] flex-1">
        <WidgetLibraryMiniPreview widget={widget} />
        <button
          type="button"
          onClick={onExpandPreviewClick(widget)}
          onKeyDown={(e) => e.stopPropagation()}
          aria-label={expandPreviewAriaLabel}
          className="absolute right-1.5 top-1.5 z-10 flex size-7 items-center justify-center rounded-full border border-edge bg-surface/95 text-content-muted opacity-0 shadow-sm backdrop-blur-sm transition-opacity duration-150 group-hover/grid-lib:opacity-100 hover:bg-surface hover:text-content focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:bg-surface-elevated/95"
        >
          <Maximize2 size={14} strokeWidth={2} aria-hidden />
        </button>
      </div>
      <div className="mt-auto flex shrink-0 items-center justify-between gap-3 border-t border-edge pt-3">
        <div className="min-w-0 flex-1">
          <h4 className="truncate text-sm font-semibold text-content" title={widget.name}>{widget.name}</h4>
          {isSelected && (
            <p className="mt-0.5 flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
              <Check size={12} aria-hidden />
              {t('widgetLibrary.detail.selected')}
            </p>
          )}
        </div>
        <DashboardButton
          type="button"
          variant="secondary"
          size="sm"
          tabIndex={disabled ? -1 : undefined}
          aria-disabled={disabled || undefined}
          className={`!h-7 shrink-0 !rounded-md !gap-1 !px-2.5 !py-0 ${disabled ? 'pointer-events-none' : ''}`}
          onClick={onActionButtonClick(widget)}
        >
          {isSelected ? (
            <>
              <Trash2 size={14} aria-hidden />
              {t('actions.remove')}
            </>
          ) : (
            `+ ${t('actions.add')}`
          )}
        </DashboardButton>
      </div>
    </div>
    </Card>
    </Tooltip>
  );
};

export default WidgetLibraryGridCard;
